import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Select,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionDto,
  AwardCriterionType,
  SourcingEventStatus,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import {
  AWARD_CRITERION_MODEL_OPTIONS,
  type AwardCriterionModel,
} from "../utils/publicProcurementOptions";
import { AwardCriterionCard } from "../components/AwardCriterionCard";
import { WeightSummary } from "../components/WeightSummary";

// t('Award criteria') t('Award criteria model') t('Lowest price is a price-only award model')
// t('Add criterion') t('Delete criterion') t('Are you sure you want to delete this criterion?')
// t('Delete') t('Criteria are locked after publication')

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

export const AwardCriteriaStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const confirmationModal = useConfirmationModal();

  const isLocked = state.status !== SourcingEventStatus.Draft;
  const awardCriteria = state.eventData.awardCriteria ?? null;
  const selectedModel: AwardCriterionModel | null =
    (["lowestPrice", "bestPriceQualityRatio"] as const).find((k) => awardCriteria?.[k] != null) ?? null;
  const awardSaving = fieldStatus("eventData.awardCriteria");
  const isSavingAwardCriteria = awardSaving === "saving" || awardSaving === "pending";
  const collectSaving = fieldStatus("eventData.collectSupplierPrice");
  const isSavingCollectSupplierPrice = collectSaving === "saving" || collectSaving === "pending";

  const bestPriceQualityRatioCriteria: AwardCriterionDto[] = useMemo(
    () => awardCriteria?.bestPriceQualityRatio?.criteria ?? [],
    [awardCriteria]
  );

  const handleModelChange = useCallback(
    (model: AwardCriterionModel) => {
      // Both sides must appear in the patch: merge-patch keeps omitted keys, so the
      // BFF would see both lowestPrice and bestPriceQualityRatio set otherwise and
      // reject with IllegalArgumentException in AwardCriteriaDto.toDomain.
      if (model === "lowestPrice") {
        updateField({
          eventData: { awardCriteria: { lowestPrice: { selected: true }, bestPriceQualityRatio: null } },
        });
      } else {
        // BFF seeds defaults via withDefaults() when bestPriceQualityRatio is set with no fields.
        updateField({
          eventData: {
            awardCriteria: { lowestPrice: null, bestPriceQualityRatio: {} },
          },
        });
      }
    },
    [updateField]
  );

  const saveCriteria = useCallback(
    (criteria: AwardCriterionDto[], debounceMs = 0) =>
      updateField(
        { eventData: { awardCriteria: { bestPriceQualityRatio: { criteria } } } },
        { debounceMs },
      ),
    [updateField]
  );

  const handleCriterionChange = useCallback(
    (index: number, next: AwardCriterionDto) => {
      const updated = bestPriceQualityRatioCriteria.map((c, i) => (i === index ? next : c));
      return saveCriteria(updated, 300);
    },
    [bestPriceQualityRatioCriteria, saveCriteria]
  );

  const handleAdd = useCallback(() => {
    const next: AwardCriterionDto = {
      type: AwardCriterionType.Quality, // catch-all default; user changes via in-card type picker
      name: "",
      description: "",
      weight: undefined,
    };
    return saveCriteria([...bestPriceQualityRatioCriteria, next]);
  }, [bestPriceQualityRatioCriteria, saveCriteria]);

  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: t("Delete criterion"),
        description: t("Are you sure you want to delete this criterion?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: async () => {
          const filtered = bestPriceQualityRatioCriteria.filter((_, i) => i !== index);
          await saveCriteria(filtered);
        },
      });
    },
    [bestPriceQualityRatioCriteria, saveCriteria, confirmationModal, t]
  );

  const selectedModelDescription = selectedModel
    ? AWARD_CRITERION_MODEL_OPTIONS.find((o) => o.value === selectedModel)?.descriptionKey
    : null;

  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="md">
          {t("Award criteria")} {isSavingAwardCriteria && <Spinner size="xs" />}
        </Heading>
      </Flex>

      {isLocked && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{t("Criteria are locked after publication")}</AlertDescription>
        </Alert>
      )}

      <FormControl mb="6" isDisabled={isLocked}>
        <Flex align="center" justify="space-between" gap="4">
          <Box>
            <Flex align="center" gap="2">
              <FormLabel mb="0">{t("Ask suppliers to enter a price")}</FormLabel>
              {isSavingCollectSupplierPrice && <Spinner size="xs" />}
            </Flex>
            <FormHelperText mt="1">
              {t(
                "When off, suppliers are not asked for a total price. You enter or override prices yourself during evaluation.",
              )}
            </FormHelperText>
          </Box>
          <Switch
            isChecked={state.eventData.collectSupplierPrice}
            onChange={(e) => updateField({ eventData: { collectSupplierPrice: e.target.checked } })}
          />
        </Flex>
      </FormControl>

      <FormControl mb="6" isDisabled={isLocked}>
        <FormLabel>{t("Award criteria model")}</FormLabel>
        <Select
          rounded="md"
          value={selectedModel ?? ""}
          onChange={(e) => handleModelChange(e.target.value as AwardCriterionModel)}
        >
          {AWARD_CRITERION_MODEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </Select>
        {selectedModelDescription && (
          <FormHelperText>{t(selectedModelDescription)}</FormHelperText>
        )}
      </FormControl>

      {selectedModel === "lowestPrice" && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{t("Lowest price is a price-only award model")}</AlertDescription>
        </Alert>
      )}

      {selectedModel === "bestPriceQualityRatio" && (
        <>
          <Box>
            {bestPriceQualityRatioCriteria.map((c, i) => (
              <AwardCriterionCard
                key={i}
                eventId={eventId}
                index={i}
                criterion={c}
                isLocked={isLocked}
                onChange={(next) => handleCriterionChange(i, next)}
                onDelete={handleDelete}
              />
            ))}
          </Box>
          <Button
            leftIcon={<AddIcon />}
            onClick={handleAdd}
            isDisabled={isLocked}
            variant="outline"
            size="sm"
            mt="2"
          >
            {t("Add criterion")}
          </Button>
          <WeightSummary criteria={bestPriceQualityRatioCriteria} />
        </>
      )}

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      {confirmationModal.modal}
    </Box>
  );
};
